import { useQuery } from "@tanstack/react-query";

import { DescribeMappableEntityResponse } from "lib/generated/integration-api";
import { useIntegrationApi } from "lib/integrationApiContext";
import { CrmPermissionRefused, isPermissionRefused } from "lib/integrations/crmSendErrors";

/**
 * The CRM fields one object offers, for the mapping form to choose from.
 *
 * Keyed on the object alone, because the answer depends only on the company's CRM — so the three
 * sections of the form share one fetch whenever they target the same object, which is the common
 * case for leads and bids.
 */
export function useDescribeMappableEntity(entityName: string, enabled: boolean) {
  const api = useIntegrationApi();

  return useQuery<DescribeMappableEntityResponse>(
    ["describeMappableEntity", entityName],
    async () => {
      try {
        return await api.describeMappableEntity({
          describeMappableEntityRequest: { entityName },
        });
      } catch (error) {
        // Recognised here, while the response body is still to hand, so every caller tells the two
        // apart with `instanceof` instead of reading it again.
        if (await isPermissionRefused(error)) throw new CrmPermissionRefused(entityName);
        throw error;
      }
    },
    {
      enabled: enabled && entityName.length > 0,
      // A CRM's schema changes about as often as someone edits it, and the form is short-lived.
      staleTime: 5 * 60 * 1000,
      // Retrying a rejected object name just delays the message explaining it.
      retry: false,
    },
  );
}
